import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Download, 
  Search,
  Eye,
  Share,
  Printer,
  RefreshCw,
  BarChart3
} from 'lucide-react';

interface Report {
  id: string;
  name: string;
  type: 'tax-summary' | 'compliance' | 'financial' | 'audit';
  entity: string;
  year: string;
  generatedDate: string;
  status: 'ready' | 'generating' | 'error';
  size: string;
  downloadUrl?: string;
}

const WealthOSReports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedYear, setSelectedYear] = useState('2025');
  const [, setGeneratingReports] = useState<Set<string>>(new Set());

  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      
      const mockReports: Report[] = [
        {
          id: '1',
          name: 'Q4 2024 Tax Summary',
          type: 'tax-summary',
          entity: 'TechCorp LLC',
          year: '2024',
          generatedDate: '2025-01-15',
          status: 'ready',
          size: '2.4 MB',
          downloadUrl: '/api/reports/download/1'
        },
        {
          id: '2',
          name: 'Annual Compliance Report',
          type: 'compliance',
          entity: 'Investment Partners',
          year: '2024',
          generatedDate: '2025-01-10',
          status: 'ready',
          size: '1.8 MB',
          downloadUrl: '/api/reports/download/2'
        },
        {
          id: '3',
          name: 'Financial Statement Analysis',
          type: 'financial',
          entity: 'TechCorp LLC',
          year: '2024',
          generatedDate: '2025-01-20',
          status: 'generating',
          size: 'Pending'
        },
        {
          id: '4',
          name: 'Audit Trail Report',
          type: 'audit',
          entity: 'All Entities',
          year: '2024',
          generatedDate: '2025-01-18',
          status: 'ready',
          size: '3.2 MB',
          downloadUrl: '/api/reports/download/4'
        }
      ];

      setReports(mockReports);
      setLoading(false);
    };

    loadReports();
  }, [selectedYear, selectedType]);

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.entity.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || report.type === selectedType;
    const matchesYear = selectedYear === 'all' || report.year === selectedYear;
    
    return matchesSearch && matchesType && matchesYear;
  });

  const handleGenerateReport = async (type: string, entity: string) => {
    const reportId = `new-${Date.now()}`;
    setGeneratingReports(prev => new Set(prev).add(reportId));
    
    setTimeout(() => {
      const newReport: Report = {
        id: reportId,
        name: `${type} Report - ${entity}`,
        type: type as Report['type'],
        entity,
        year: selectedYear,
        generatedDate: new Date().toISOString().split('T')[0],
        status: 'ready',
        size: '1.5 MB',
        downloadUrl: `/api/reports/download/${reportId}`
      };
      
      setReports(prev => [newReport, ...prev]);
      setGeneratingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }, 3000);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'tax-summary':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'compliance':
        return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      case 'financial':
        return 'text-purple-400 bg-purple-500/20 border-purple-500/30';
      case 'audit':
        return 'text-orange-400 bg-orange-500/20 border-orange-500/30';
      default:
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'generating':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'error':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
      default:
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-900 p-6 overflow-auto">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-500/20 rounded-lg">
              <FileText className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white text-drop-shadow">Reports & Exports</h1>
              <p className="text-slate-400">Generate and download tax and financial reports</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleGenerateReport('tax-summary', 'TechCorp LLC')}
              className="px-4 py-2 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 rounded-lg text-teal-400 font-medium transition-colors flex items-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              Generate Report
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            >
              <option value="all">All Types</option>
              <option value="tax-summary">Tax Summary</option>
              <option value="compliance">Compliance</option>
              <option value="financial">Financial</option>
              <option value="audit">Audit</option>
            </select>
            
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            >
              <option value="all">All Years</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {filteredReports.map((report, index) => (
            <motion.div
              key={report.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl rounded-lg p-6 border border-slate-700/50"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <FileText className="w-6 h-6 text-slate-300" />
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{report.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span>{report.entity}</span>
                      <span>•</span>
                      <span>{report.year}</span>
                      <span>•</span>
                      <span>Generated {report.generatedDate}</span>
                      <span>•</span>
                      <span>{report.size}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(report.type)}`}>
                    {report.type.replace('-', ' ')}
                  </span>
                  
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(report.status)}`}>
                    {report.status === 'generating' && (
                      <RefreshCw className="w-3 h-3 animate-spin inline mr-1" />
                    )}
                    {report.status}
                  </span>
                  
                  <div className="flex items-center gap-2">
                    {report.status === 'ready' && (
                      <>
                        <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
                          <Share className="w-4 h-4" />
                        </button>
                        
                        <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
                          <Printer className="w-4 h-4" />
                        </button>
                        
                        <button className="px-3 py-2 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 rounded-lg text-teal-400 text-sm font-medium transition-colors flex items-center gap-2">
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredReports.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No reports found</h3>
            <p className="text-slate-500">Try adjusting your search criteria or generate a new report.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WealthOSReports;
